import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  User, Phone, Mail, Building2, CreditCard, MapPin, Calendar,
  Smartphone, DollarSign, Shield, Clock, Users, FileText,
  ArrowRightLeft, Home, Hash, Briefcase, CheckCircle2, XCircle,
  TrendingUp, Receipt
} from 'lucide-react';
import { formatarCNPJ, formatarMoeda, formatarTelefone } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { PhoneWithOperator } from '@/components/ui/phone-with-operator';
import { MAX_LINHAS_MOVEL } from '@/lib/leadImportUtils';
import { BadgeStatusAptidao } from './BadgeStatusAptidao';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

interface LeadDados360TTProps {
  lead: LeadCarteira;
  onLigar: (telefone: string) => void;
  campanhaNome?: string | null;
}

// Helper: InfoRow simples
function InfoRow({ label, value, icon: Icon }: { label: string; value?: string | number | null; icon?: any }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
      </div>
      <span className="text-sm font-medium text-right max-w-[60%] truncate">
        {value || <span className="text-muted-foreground/50">—</span>}
      </span>
    </div>
  );
}

// Helper: telefone com click-to-call e a operadora consultada na API
function TelefoneRow({ label, telefone, leadId }: { label: string; telefone?: string | null; leadId: string }) {
  if (!telefone) return null;
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Phone className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <PhoneWithOperator phone={telefone} variant="badge-only" />
        <ClickToCall telefone={telefone} leadId={leadId}>
          <span className="text-primary hover:underline font-medium text-sm flex items-center gap-1 cursor-pointer font-mono">
            {formatarTelefone(telefone)}
            <Phone className="w-3 h-3" />
          </span>
        </ClickToCall>
      </div>
    </div>
  );
}

// Helper: parse valor monetário de string (detecta formato automaticamente)
function parseValor(val: any): number {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const str = String(val).replace(/[R$\s]/g, '').trim();
  if (!str) return 0;
  const temVirgula = str.includes(',');
  const temPonto = str.includes('.');
  let limpo: string;
  if (temVirgula && temPonto) {
    // Formato BR: "2.118,64" → remover pontos, trocar vírgula
    limpo = str.replace(/\./g, '').replace(',', '.');
  } else if (temVirgula) {
    // Só vírgula: "59,89" → trocar vírgula por ponto
    limpo = str.replace(',', '.');
  } else {
    // Só ponto ou nenhum: "59.89" → ponto é decimal, manter
    limpo = str;
  }
  return parseFloat(limpo) || 0;
}

// Helper: formatar CPF
function formatarCPF(cpf?: string | null): string {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return cpf;
}

export function LeadDados360TT({ lead, onLigar, campanhaNome }: LeadDados360TTProps) {
  const dados = (lead.dados_discadora as any) || {};

  // === Cálculos inteligentes ===
  const analysis = useMemo(() => {
    // Vai até Movel 18: as bases de maio/junho trazem até 18 linhas na mesma conta
    const linhasMovel = Array.from({ length: MAX_LINHAS_MOVEL }, (_, i) => dados[`movel_${i + 1}`]);
    const linhasPreenchidas = linhasMovel.filter(l => l && String(l).trim());
    const qtdLinhas = linhasPreenchidas.length;

    const valorMovel = parseValor(dados.valor_movel);
    const valorCombo = parseValor(dados.valor_combo);
    const valorResidencial = parseValor(dados.valor_residencial);
    const valorTotal = valorMovel + valorCombo + valorResidencial;

    // Detectar tipo
    let tipoCliente = dados.tipo_cliente;
    if (!tipoCliente) {
      if (valorMovel > 0 && valorResidencial > 0) tipoCliente = 'COMBO';
      else if (valorResidencial > 0) tipoCliente = 'RESIDENCIAL';
      else tipoCliente = 'MÓVEL';
    }

    // Sócios parsing
    const sociosStr = dados.socios || '';
    const sociosList = sociosStr ? sociosStr.split(';').map((s: string) => s.trim()).filter(Boolean) : [];
    const telefone1Socio = dados.telefone1_socio || '';
    const telefone2Socio = dados.telefone2_socio || '';

    return {
      linhasMovel: linhasPreenchidas,
      qtdLinhas,
      valorMovel,
      valorCombo,
      valorResidencial,
      valorTotal,
      tipoCliente,
      sociosList,
      telefone1Socio,
      telefone2Socio,
    };
  }, [dados]);

  const tipoBadgeColor = analysis.tipoCliente === 'COMBO'
    ? 'bg-muted text-primary dark:bg-muted/30 dark:text-primary'
    : analysis.tipoCliente === 'RESIDENCIAL'
    ? 'bg-warning/10 text-warning dark:bg-warning/[0.08] dark:text-warning'
    : 'bg-info/10 text-info dark:bg-info/[0.08] dark:text-info';

  const situacaoAtiva = dados.situacao_cadastral_pj?.toLowerCase()?.includes('ativa');

  return (
    <div className="space-y-4">
      {/* ═══ SEÇÃO 1: RESUMO EXECUTIVO ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Tipo Cliente */}
        <div className="p-3 rounded-lg bg-muted/50 border text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Tipo</span>
          <div className="mt-1">
            <Badge className={tipoBadgeColor}>{analysis.tipoCliente}</Badge>
          </div>
        </div>

        {/* Qtd Linhas */}
        <div className="p-3 rounded-lg bg-warning/[0.08] border border-warning/50 text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Linhas</span>
          <p className="text-2xl font-bold text-warning dark:text-warning">{analysis.qtdLinhas}</p>
          <span className="text-[10px] text-muted-foreground">móveis</span>
        </div>

        {/* Valor Total */}
        <div className="p-3 rounded-lg bg-success/[0.08] border border-success/50 text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total</span>
          <p className="text-lg font-bold text-success dark:text-success">
            {analysis.valorTotal > 0 ? formatarMoeda(analysis.valorTotal) : '—'}
          </p>
        </div>

        {/* Data Fatura */}
        <div className="p-3 rounded-lg bg-muted/50 border text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Fatura</span>
          <p className="text-sm font-bold mt-1">{dados.data_fatura || '—'}</p>
        </div>

        {/* Status */}
        <div className="p-3 rounded-lg bg-muted/50 border text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</span>
          <div className="mt-1">
            {dados.status_planilha ? (
              <Badge variant="outline" className={
                dados.status_planilha.toLowerCase().includes('sucesso') || dados.status_planilha.toLowerCase().includes('conectado')
                  ? 'bg-success/10 text-success dark:bg-success/[0.08] dark:text-success border-success'
                  : 'bg-warning/10 text-warning dark:bg-warning/[0.08] dark:text-warning border-warning'
              }>
                {dados.status_planilha}
              </Badge>
            ) : <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </div>

        {/* Mudança Endereço */}
        <div className="p-3 rounded-lg bg-muted/50 border text-center">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Mud. End.</span>
          <div className="mt-1">
            {dados.mudanca_endereco ? (
              <Badge variant="outline" className={
                dados.mudanca_endereco.toLowerCase() === 'sim'
                  ? 'bg-danger/10 text-danger border-danger'
                  : ''
              }>
                {dados.mudanca_endereco}
              </Badge>
            ) : <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </div>

        {/* Campanha */}
        {campanhaNome && (
          <div className="p-3 rounded-lg bg-muted/50 border text-center col-span-2 md:col-span-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Campanha</span>
            <p className="text-xs font-semibold mt-1 truncate" title={campanhaNome}>{campanhaNome}</p>
          </div>
        )}
      </div>

      {/* ═══ SEÇÃO 2: CLIENTE PESSOA FÍSICA ═══ */}
      <Card className="border-info/40 bg-info/[0.08]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-info dark:text-info">
            <User className="w-4 h-4" /> Cliente Pessoa Física (Titular da Conta)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="Nome" value={lead.nome_proprietario} icon={User} />
            <InfoRow label="CPF" value={formatarCPF(lead.doc_cpf)} icon={CreditCard} />
            <InfoRow label="Email" value={lead.email} icon={Mail} />
            <div className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Shield className="w-4 h-4" />
                <span>Aptidão</span>
              </div>
              <BadgeStatusAptidao statusAptidao={lead.status_aptidao || 'APTO'} />
            </div>
          </div>

          {/* Endereço completo */}
          {(lead.endereco_empresa || dados.endereco_empresa || dados.numero_endereco || dados.bairro || dados.novo_endereco) && (
            <>
              <Separator className="my-3" />
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Endereço
              </h4>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
                <InfoRow label="Logradouro" value={lead.endereco_empresa || dados.endereco_empresa} />
                <InfoRow label="Número" value={dados.numero_endereco} icon={Hash} />
                <InfoRow label="Complemento" value={dados.complemento} />
                <InfoRow label="Bairro" value={dados.bairro} />
                <InfoRow label="Cidade/UF" value={
                  [dados.cidade, lead.uf].filter(Boolean).join('/') || undefined
                } icon={MapPin} />
                <InfoRow label="CEP" value={dados.cep} />
              </div>

              {dados.novo_endereco && (
                <div className="mt-2 p-2 rounded-lg bg-danger/[0.08] border border-danger/40">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-danger">
                    <ArrowRightLeft className="w-3 h-3" /> Endereço da mudança
                  </div>
                  <p className="text-sm font-medium mt-0.5">{dados.novo_endereco}</p>
                </div>
              )}
            </>
          )}

          {/* Telefones PF */}
          <Separator className="my-3" />
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Contatos PF
          </h4>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
            <TelefoneRow label="Telefone 1" telefone={lead.telefone} leadId={lead.id} />
            <TelefoneRow label="Telefone 2" telefone={lead.telefone_2} leadId={lead.id} />
            <TelefoneRow label="Telefone 3" telefone={lead.telefone_3} leadId={lead.id} />
          </div>
        </CardContent>
      </Card>

      {/* ═══ SEÇÃO 3: LINHAS DA CONTA CPF ═══ */}
      <Card className="border-warning/40 bg-warning/[0.08]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-warning dark:text-warning">
            <Smartphone className="w-4 h-4" /> Linhas da Conta CPF
            <Badge variant="outline" className="ml-auto text-xs border-warning text-warning dark:text-warning">
              {analysis.qtdLinhas} {analysis.qtdLinhas === 1 ? 'linha' : 'linhas'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analysis.qtdLinhas > 0 ? (
            <>
              <p className="text-xs text-muted-foreground mb-3">
                Linhas móveis do CPF que serão migradas para o CNPJ:
              </p>
              <div className="flex flex-wrap gap-2">
                {analysis.linhasMovel.map((val, i) => {
                  const numero = String(val);
                  return (
                    <div key={`movel-${i}`} className="flex items-center gap-1">
                      <ClickToCall telefone={numero} leadId={lead.id}>
                        <Badge
                          variant="outline"
                          className="font-mono cursor-pointer hover:bg-warning/10 dark:hover:bg-warning/[0.08] transition-colors text-sm py-1.5 px-3 border-warning"
                        >
                          📱 {formatarTelefone(numero)}
                          <Phone className="w-3 h-3 ml-1.5 text-warning" />
                        </Badge>
                      </ClickToCall>
                      {/* operadora vem da API (cache de 24h) — é ela que diz de quem vamos portar */}
                      <PhoneWithOperator phone={numero} variant="badge-only" />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-warning/5 dark:bg-warning/10 rounded-lg border border-warning/50">
              <Home className="w-5 h-5 text-warning" />
              <div>
                <p className="text-sm font-medium text-warning dark:text-warning">Cliente apenas Residencial</p>
                <p className="text-xs text-muted-foreground">Sem linhas móveis para migrar — apenas plano residencial/combo</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ SEÇÃO 4: EMPRESA PARA MIGRAÇÃO ═══ */}
      <Card className="border-success/40 bg-success/[0.08]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-success dark:text-success">
            <Building2 className="w-4 h-4" /> Empresa para Migração (PJ)
            {situacaoAtiva !== undefined && (
              <Badge variant="outline" className={
                situacaoAtiva
                  ? 'ml-2 bg-success/10 text-success border-success dark:bg-success/[0.08] dark:text-success'
                  : 'ml-2 bg-danger/10 text-danger border-danger'
              }>
                {situacaoAtiva ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                {dados.situacao_cadastral_pj || 'N/A'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="CNPJ" value={formatarCNPJ(lead.cnpj)} icon={CreditCard} />
            <InfoRow label="Razão Social" value={lead.razao_social} icon={Building2} />
            <InfoRow label="CNAE Principal" value={dados.cnae_principal} icon={Briefcase} />
            <InfoRow label="Natureza Jurídica" value={dados.natureza_juridica} icon={FileText} />
            <InfoRow label="Porte" value={dados.porte_empresa} icon={TrendingUp} />
            <InfoRow label="Simples Nacional" value={dados.simples_nacional} icon={Shield} />
            <InfoRow label="MEI" value={dados.mei} />
            <InfoRow label="Data Sit. Cadastral" value={dados.data_situacao_cadastral} icon={Calendar} />
          </div>

          {/* Localização PJ */}
          {(dados.cidade_pj || dados.estado_pj) && (
            <>
              <Separator className="my-3" />
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Localização PJ
              </h4>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
                <InfoRow label="Cidade" value={dados.cidade_pj} icon={MapPin} />
                <InfoRow label="Estado" value={dados.estado_pj} />
              </div>
            </>
          )}

          {/* Contatos PJ */}
          {(dados.telefone_pj_1 || dados.telefone_pj_2 || dados.email_pj) && (
            <>
              <Separator className="my-3" />
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Contatos PJ
              </h4>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
                <TelefoneRow label="Tel. PJ 1" telefone={dados.telefone_pj_1} leadId={lead.id} />
                <TelefoneRow label="Tel. PJ 2" telefone={dados.telefone_pj_2} leadId={lead.id} />
              </div>
            </>
          )}

          {/* Sócios */}
          {analysis.sociosList.length > 0 && (
            <>
              <Separator className="my-3" />
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Sócios
              </h4>
              <div className="flex flex-col gap-2">
                {analysis.sociosList.map((socio: string, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm py-1 px-2">
                      <User className="w-3 h-3 mr-1" /> {socio}
                    </Badge>
                    {i === 0 && analysis.telefone1Socio && (
                      <ClickToCall telefone={analysis.telefone1Socio} leadId={lead.id}>
                        <span className="text-primary hover:underline text-xs font-mono cursor-pointer flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {formatarTelefone(analysis.telefone1Socio)}
                        </span>
                      </ClickToCall>
                    )}
                    {i === 1 && analysis.telefone2Socio && (
                      <ClickToCall telefone={analysis.telefone2Socio} leadId={lead.id}>
                        <span className="text-primary hover:underline text-xs font-mono cursor-pointer flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {formatarTelefone(analysis.telefone2Socio)}
                        </span>
                      </ClickToCall>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ═══ SEÇÃO 5: VALORES DO PLANO ATUAL ═══ */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Valores do Plano Atual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {analysis.valorMovel > 0 && (
              <div className="p-3 bg-info/[0.08] rounded-lg text-center border border-info/50">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Móvel</span>
                <p className="text-lg font-bold text-info dark:text-info">{formatarMoeda(analysis.valorMovel)}</p>
              </div>
            )}
            {analysis.valorCombo > 0 && (
              <div className="p-3 bg-muted/50 dark:bg-muted/10 rounded-lg text-center border border-border/50">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Combo</span>
                <p className="text-lg font-bold text-primary dark:text-primary">{formatarMoeda(analysis.valorCombo)}</p>
              </div>
            )}
            {analysis.valorResidencial > 0 && (
              <div className="p-3 bg-warning/[0.08] rounded-lg text-center border border-warning/50">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Residencial</span>
                <p className="text-lg font-bold text-warning dark:text-warning">{formatarMoeda(analysis.valorResidencial)}</p>
              </div>
            )}
            <div className="p-3 bg-success/[0.08] rounded-lg text-center border border-success/50">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total</span>
              <p className="text-xl font-bold text-success dark:text-success">
                {analysis.valorTotal > 0 ? formatarMoeda(analysis.valorTotal) : formatarMoeda(Number(lead.valor_fatura_atual) || 0)}
              </p>
            </div>
          </div>

          {/* Data da Fatura */}
          {dados.data_fatura && (
            <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground border-t pt-3">
              <span className="flex items-center gap-1"><Receipt className="w-4 h-4" /> Data da Fatura</span>
              <span className="font-medium">{dados.data_fatura}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
